import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, CheckCircle2, AlertTriangle, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

export default function AdminSbtTrendingTopics() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/sbt-site/trending-topics-status`);
      setStatus(r.data);
    } catch (e) {
      toast.error("Failed to load trending-topics status");
    } finally {
      setLoading(false);
    }
  };

  const triggerRefresh = async (industry) => {
    setRefreshing(true);
    try {
      const url = industry
        ? `${API}/api/admin/sbt-site/refresh-trending-topics?industry=${encodeURIComponent(industry)}`
        : `${API}/api/admin/sbt-site/refresh-trending-topics`;
      await axios.post(url);
      toast.success(industry ? `Refreshed: ${industry}` : "Full refresh queued");
      await load();
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <Card data-testid="trending-topics-tile">
      <CardHeader>
        <CardTitle>SBT Trending Topics · Refresh Status</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">Loading…</CardContent>
    </Card>
  );
  if (!status) return null;

  const coveragePct = Math.round((status.total_covered / (status.total_configured || 1)) * 100);
  const healthy = status.low_coverage_count === 0 && status.missing_industries.length === 0;

  return (
    <Card data-testid="trending-topics-tile" className="border-l-4" style={{ borderLeftColor: healthy ? "#16a34a" : "#f59e0b" }}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              SBT Trending Topics · Refresh Status
              {healthy ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              )}
            </CardTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Weekly Mon 03:15 UTC · Powered by Google News RSS + Claude Sonnet 4.6
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="trending-topics-reload">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Reload
            </Button>
            <Button size="sm" onClick={() => triggerRefresh()} disabled={refreshing} data-testid="trending-topics-refresh-all">
              Refresh all
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Key stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="p-3 rounded-md bg-muted/40">
            <div className="text-2xl font-bold text-slate-900" data-testid="trending-coverage">
              {status.total_covered}/{status.total_configured}
            </div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5">
              Industries · {coveragePct}%
            </div>
          </div>
          <div className="p-3 rounded-md bg-muted/40">
            <div className="text-2xl font-bold text-slate-900" data-testid="trending-with-50">
              {status.with_50_topics}
            </div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5">
              With full 50 topics
            </div>
          </div>
          <div className="p-3 rounded-md bg-muted/40">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5" data-testid="trending-last">
              <Clock className="h-3.5 w-3.5" /> {fmtDate(status.last_refresh)}
            </div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5">
              Last refresh
            </div>
          </div>
          <div className="p-3 rounded-md bg-muted/40">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5" data-testid="trending-next">
              <Clock className="h-3.5 w-3.5" /> {fmtDate(status.next_refresh)}
            </div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5">
              Next auto-refresh
            </div>
          </div>
        </div>

        {/* Issues to retry */}
        {(status.low_coverage_industries.length > 0 || status.missing_industries.length > 0) && (
          <div className="border rounded-md p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-amber-700 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" /> Needs attention
              </div>
              <Badge variant="secondary">{status.low_coverage_count + status.missing_industries.length}</Badge>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {status.missing_industries.map((ind) => (
                <button
                  key={"m-" + ind}
                  onClick={() => triggerRefresh(ind)}
                  disabled={refreshing}
                  data-testid={`retry-industry-${ind}`}
                  className="text-xs px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                  title="Missing — click to retry"
                >
                  {ind} <span className="opacity-60">· 0</span>
                </button>
              ))}
              {status.low_coverage_industries.map((li) => (
                <button
                  key={"l-" + li.industry}
                  onClick={() => triggerRefresh(li.industry)}
                  disabled={refreshing}
                  data-testid={`retry-industry-${li.industry}`}
                  className="text-xs px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100"
                  title="Low coverage — click to retry"
                >
                  {li.industry} <span className="opacity-60">· {li.topics_count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Live view link */}
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <ExternalLink className="h-3 w-3" />
          Live on the marketing site under Media Kit → Top 50 Trending Topics
        </div>
      </CardContent>
    </Card>
  );
}
